"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { registerServiceWorker } from "@/lib/pwa/service-worker-registration";
import { InstallPrompt } from "@/components/pwa/install-prompt";
// import { UpdateNotification } from "@/components/pwa/update-notification";
import { NotificationPermissionPrompt } from "@/components/notifications/notification-permission-prompt";
import { AutoUpdateListener } from "@/components/pwa/auto-update-listener";

/**
 * Clear dynamic page cache via service worker
 * Call this when coming back online to ensure fresh data
 */
function clearDynamicCache() {
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: "CLEAR_DYNAMIC_CACHE",
    });
    console.log("[PWA Provider] Requested dynamic cache clear");
  }
}

/**
 * PWA Provider Component
 * Handles service worker registration, FCM setup, and PWA prompts
 */
export function PWAProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const wasOffline = useRef(false);

  useEffect(() => {
    // Register service worker
    if (process.env.NODE_ENV === "production") {
      registerServiceWorker();
    }

    // Track initial online state
    wasOffline.current = !navigator.onLine;

    // Handle coming back online - clear cache and refresh
    const handleOnline = () => {
      console.log("[PWA Provider] Back online");
      if (wasOffline.current) {
        // Clear dynamic cache to ensure fresh data on next navigation
        clearDynamicCache();
        // Refresh current page to get fresh data
        router.refresh();
        console.log("[PWA Provider] Triggered refresh after coming online");
      }
      wasOffline.current = false;
    };

    const handleOffline = () => {
      console.log("[PWA Provider] Gone offline");
      wasOffline.current = true;
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Listen for navigation messages from service worker (notification clicks)
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "NAVIGATE") {
        const url = event.data.url;
        console.log("[PWA Provider] Navigating to:", url);

        // Navigate to the URL
        if (url.startsWith("http://") || url.startsWith("https://")) {
          // External URL - open in new tab
          window.open(url, "_blank");
        } else {
          // Internal path - use Next.js router
          router.push(url);
        }
      }
    };

    navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
    };
  }, [router]);

  return (
    <>
      {children}
      {/* <InstallPrompt /> */}
      <AutoUpdateListener />
      {/* <NotificationPermissionPrompt /> */}
    </>
  );
}
